import React, { ReactNode, useEffect } from 'react';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

const DEFAULT_HEIGHT_DURATION = 200;
const DEFAULT_OPACITY_DURATION = 150;

type FadeInCollapseProps = {
    visible: boolean;
    height: number;
    heightDuration?: number;
    opacityDuration?: number;
    children?: ReactNode;
};

/**
 * Animates content visibility by collapsing/expanding height and fading in/out opacity
 * When visible becomes true, expands height and fades in content.
 * When visible becomes false, collapses height and fades out content.
 */
export const FadeInCollapse = ({
    visible,
    height,
    heightDuration = DEFAULT_HEIGHT_DURATION,
    opacityDuration = DEFAULT_OPACITY_DURATION,
    children,
}: FadeInCollapseProps): React.JSX.Element => {
    const animatedHeight = useSharedValue(visible ? height : 0);
    const opacity = useSharedValue(visible ? 1 : 0);

    useEffect(() => {
        animatedHeight.value = withTiming(visible ? height : 0, { duration: heightDuration });
        opacity.value = withTiming(visible ? 1 : 0, { duration: opacityDuration });
    }, [visible, height, heightDuration, opacityDuration, animatedHeight, opacity]);

    const style = useAnimatedStyle(() => ({
        height: animatedHeight.value,
        opacity: opacity.value,
        overflow: 'hidden' as const,
    }));

    return <Animated.View style={style}>{children}</Animated.View>;
};

type CrossfadeProps = {
    showFirst: boolean;
    first: ReactNode;
    firstHeight: number;
    second: ReactNode;
    secondHeight: number;
    heightDuration?: number;
    opacityDuration?: number;
};

/**
 * Crossfades between two views with height animation
 *
 * Crossfade === make one view fade in while the other fades out, and animate container height to match the visible view
 *
 * Both views are stacked absolutely, with opacity and pointerEvents controlled by showFirst.
 * Container height animates to match the currently visible view.
 */
export const Crossfade = ({
    showFirst,
    first,
    firstHeight,
    second,
    secondHeight,
    heightDuration = DEFAULT_HEIGHT_DURATION,
    opacityDuration = DEFAULT_OPACITY_DURATION,
}: CrossfadeProps): React.JSX.Element => {
    const containerHeight = useSharedValue(showFirst ? firstHeight : secondHeight);
    const firstOpacity = useSharedValue(showFirst ? 1 : 0);
    const secondOpacity = useSharedValue(showFirst ? 0 : 1);
    const firstVisible = useSharedValue(showFirst ? 1 : 0);

    useEffect(() => {
        const targetHeight = showFirst ? firstHeight : secondHeight;
        containerHeight.value = withTiming(targetHeight, { duration: heightDuration });
        firstOpacity.value = withTiming(showFirst ? 1 : 0, { duration: opacityDuration });
        secondOpacity.value = withTiming(showFirst ? 0 : 1, { duration: opacityDuration });
        firstVisible.value = showFirst ? 1 : 0;
    }, [
        showFirst,
        firstHeight,
        secondHeight,
        heightDuration,
        opacityDuration,
        containerHeight,
        firstOpacity,
        secondOpacity,
        firstVisible,
    ]);

    const containerStyle = useAnimatedStyle(() => ({
        height: containerHeight.value,
        overflow: 'hidden' as const,
    }));

    const firstStyle = useAnimatedStyle(() => ({
        opacity: firstOpacity.value,
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        pointerEvents: firstVisible.value === 1 ? ('auto' as const) : ('none' as const),
    }));

    const secondStyle = useAnimatedStyle(() => ({
        opacity: secondOpacity.value,
        position: 'absolute' as const,
        top: 0,
        left: 0,
        right: 0,
        pointerEvents: firstVisible.value === 1 ? ('none' as const) : ('auto' as const),
    }));

    return (
        <Animated.View style={containerStyle}>
            <Animated.View style={firstStyle}>{first}</Animated.View>
            <Animated.View style={secondStyle}>{second}</Animated.View>
        </Animated.View>
    );
};
